import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #gridObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    const storedImageView = sessionStorage.getItem('product-grid-image');
    if (storedImageView === 'second') this.#setImageView(storedImageView);

    const { grid } = this.refs;
    if (grid) {
      this.#gridObserver = new MutationObserver(this.#handleGridMutation);
      this.#gridObserver.observe(grid, { childList: true });
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridObserver?.disconnect();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Updates which product image is shown by default on every card in the grid.
   *
   * @param {Event} event
   */
  updateImage({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    const value = target.checked ? 'second' : 'default';
    this.#setImageView(value);
    sessionStorage.setItem('product-grid-image', value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Sets which product image is shown by default on every card in the grid.
   *
   * Note: this deliberately calls slideshow.select() rather than setting the
   * initial-slide attribute. Slideshow's own connect logic sets its internal
   * `current` index straight from initial-slide without updating the DOM, so
   * a later select() to that same index becomes a same-slide no-op and the
   * slide never actually flips. Calling select() directly (after the
   * slideshow has already connected with its real current index) avoids that.
   *
   * @param {string} value - Either 'default' or 'second'.
   */
  #setImageView(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-image', value);

    const targetIndex = value === 'second' ? 1 : 0;
    for (const slideshow of grid.querySelectorAll('slideshow-component')) {
      slideshow.select(targetIndex, undefined, { animate: false });
    }

    const toggles = this.querySelectorAll('input[data-image-view-toggle]');
    for (const toggle of toggles) {
      if (toggle instanceof HTMLInputElement) toggle.checked = value === 'second';
    }
  }

  /**
   * Applies the current image view to product cards added to the grid after
   * initial render (infinite scroll, pagination, or filter updates).
   *
   * @param {MutationRecord[]} mutations
   */
  #handleGridMutation = (mutations) => {
    if (sessionStorage.getItem('product-grid-image') !== 'second') return;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        const slideshows = node.matches('slideshow-component')
          ? [node]
          : node.querySelectorAll('slideshow-component');

        for (const slideshow of slideshows) {
          slideshow.select(1, undefined, { animate: false });
        }
      }
    }
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
